import { StyledIcon } from 'fitify-ui/src/Icon/Icon.Styled'
import { css, type DefaultTheme, styled } from 'styled-components'

import { BREAKPOINTS } from '../../../utils/styled'

import type {
  ButtonProps,
  IconButtonProps,
  TextButtonProps,
} from './Button.Types'

const commonButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;

  &[disabled] {
    cursor: not-allowed;
    pointer-events: none;
  }
`

const getButtonVariantColors = (
  variant: ButtonProps['variant'],
  theme: DefaultTheme
) => {
  switch (variant) {
    case 'primary':
      return {
        enabled: {
          color: theme.colors['N Content/Primary'],
          background: theme.colors['N Surface/Primary'],
        },
        hover: {
          color: theme.colors['N Content/Secondary'],
          background: theme.colors['N Surface/Primary'],
        },
        pressed: {
          color: theme.colors['N Content/Primary'],
          background: theme.colors['N Surface/Primary'],
          outline: theme.colors['N Outline/Brand Selected'],
        },
        disabled: {
          color: theme.colors['N Content/Disabled'],
          background: theme.colors['N Surface/Primary'],
        },
        loading: {
          color: theme.colors['N Content/Primary'],
          background: theme.colors['N Surface/Primary'],
          outline: theme.colors['N Outline/Brand Selected'],
        },
      }
    case 'brand':
    default:
      return {
        enabled: {
          color: theme.colors['N Content/Inverse'],
          background: theme.colors['N Button/Enable'],
        },
        hover: {
          color: theme.colors['N Content/Inverse'],
          background: theme.colors['N Button/Hover'],
        },
        pressed: {
          color: theme.colors['N Content/Inverse'],
          background: theme.colors['N Button/Pressed'],
        },
        disabled: {
          color: theme.colors['N Content/Inverse Disabled'],
          background: theme.colors['N Button/Disabled'],
        },
        loading: {
          color: theme.colors['N Content/Inverse'],
          background: theme.colors['N Button/Pressed'],
        },
      }
  }
}

export const StyledButton = styled.button<{
  $fitWidth?: number
  $isLoading?: boolean
  $maxWidth?: string
  $size: ButtonProps['size']
  $variant: ButtonProps['variant']
}>`
  ${commonButtonStyles};
  ${({ $maxWidth, $fitWidth }) => {
    return $maxWidth
      ? css`
          width: ${$fitWidth ? `${$fitWidth}px` : 'fit-content'};
          max-width: ${$maxWidth};
          flex: 0 0 auto;
        `
      : css`
          width: 100%;
        `
  }}
  min-height: ${({ theme, $size }) => {
    switch ($size) {
      case 'medium':
        return '2.5rem'
      case 'large':
      default:
        return theme.dynamicSizes.mobile['Height/Button']
    }
  }};
  padding: 0.25rem 0.875rem;
  border-radius: ${({ theme }) => theme.radius['Radius FULL']}px;
  gap: 0.75rem;
  outline: 1px solid transparent;

  @media ${BREAKPOINTS.MD} {
    height: ${({ theme }) => theme.dynamicSizes.desktop['Height/Button']};
  }

  ${StyledIcon} {
    width: 1.375rem;
    height: 1.375rem;
    color: inherit;
  }

  ${({ $isLoading, $variant, theme }) => {
    const colors = getButtonVariantColors($variant, theme)

    return css`
      background: ${
        $isLoading ? colors.loading.background : colors.enabled.background
      };
      color: ${$isLoading ? colors.loading.color : colors.enabled.color};
      ${
        $isLoading && colors.loading.outline
          ? css`
              outline-color: ${colors.loading.outline};
            `
          : null
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover,
        &:focus {
          background: ${colors.hover.background};
          color: ${colors.hover.color};
        }
      }

      &:active {
        background: ${colors.pressed.background};
        color: ${colors.pressed.color};
        ${
          colors.pressed.outline
            ? css`
                outline-color: ${colors.pressed.outline};
              `
            : null
        }
      }

      &[disabled] {
        background: ${colors.disabled.background};
        color: ${colors.disabled.color};
      }
    `
  }}
`

const getTextButtonVariantColors = (
  variant: TextButtonProps['variant'],
  theme: DefaultTheme
) => {
  switch (variant) {
    case 'secondary':
      return {
        enabled: theme.colors['N Content/Secondary'],
        hover: theme.colors['N Content/Tertiary'],
        pressed: theme.colors['N Content/Primary'],
        disabled: theme.colors['N Content/Disabled'],
      }
    case 'brand':
    default:
      return {
        enabled: theme.colors['N Button/Enable'],
        hover: theme.colors['N Button/Hover'],
        pressed: theme.colors['N Button/Pressed'],
        disabled: theme.colors['N Button/Disabled'],
      }
  }
}

export const StyledTextButton = styled.button<{
  $isLoading?: boolean
  $variant: TextButtonProps['variant']
}>`
  ${commonButtonStyles};
  width: 100%;
  padding: 0.4375rem 0.625rem;
  background: transparent;
  gap: 0.25rem;

  ${({ $isLoading, theme }) => {
    if ($isLoading) {
      return css`
        ${StyledIcon} {
          width: 1.375rem;
          height: 1.375rem;
          color: inherit;
        }
      `
    }

    return css`
      ${StyledIcon} {
        width: ${theme.dynamicSizes.mobile['Typography/T2']};
        height: ${theme.dynamicSizes.mobile['Typography/T2']};
        color: inherit;

        @media ${BREAKPOINTS.MD} {
          width: ${theme.dynamicSizes.desktop['Typography/T2']};
          height: ${theme.dynamicSizes.desktop['Typography/T2']};
        }
      }
    `
  }}

  ${({ $variant, theme }) => {
    const color = getTextButtonVariantColors($variant, theme)

    return css`
      color: ${color.enabled};

      @media (hover: hover) and (pointer: fine) {
        &:hover,
        &:focus {
          color: ${color.hover};
        }
      }

      &:active {
        color: ${color.pressed};
      }

      &[disabled] {
        color: ${color.disabled};
      }
    `
  }}
`

export const StyledIconButton = styled.button<{
  $size: IconButtonProps['size']
  $variant: IconButtonProps['variant']
}>`
  ${commonButtonStyles};
  ${({ $size }) => {
    const sizes = (() => {
      switch ($size) {
        case 'x-large':
          return {
            button: '2.75rem',
            icon: '1.375rem',
          }
        case 'large':
          return {
            button: '2.5rem',
            icon: '1rem',
          }
        case 'medium':
        default:
          return {
            button: '2rem',
            icon: '1rem',
          }
      }
    })()
    return css`
      width: ${sizes.button};
      height: ${sizes.button};
      flex: 0 0 ${sizes.button};

      ${StyledIcon} {
        width: ${sizes.icon};
        height: ${sizes.icon};
        color: inherit;
      }
    `
  }}

  border-radius: ${({ theme }) => theme.radius['Radius FULL']}px;

  ${({ $variant, theme }) => {
    const primaryBg =
      $variant === 'transparent'
        ? theme.colors['N Surface/BG Blur Primary']
        : theme.colors['N Surface/Secondary']
    const secondaryBg =
      $variant === 'transparent'
        ? theme.colors['N Surface/BG Blur Base']
        : theme.colors['N Surface/Secondary']

    return css`
      background: ${primaryBg};
      color: ${theme.colors['N Content/Primary']};
      outline: ${
        $variant === 'transparent'
          ? `1px solid ${theme.colors['N Outline/Primary']}`
          : 'none'
      };

      @media (hover: hover) and (pointer: fine) {
        &:hover,
        &:focus {
          background: ${secondaryBg};
          color: ${theme.colors['N Content/Secondary']};
        }
      }

      &:active {
        background: ${secondaryBg};
        color: ${theme.colors['N Content/Primary']};
      }

      &[disabled] {
        background: ${secondaryBg};
        color: ${theme.colors['N Content/Disabled']};
      }
    `
  }}
`

export const StyledAnchor = styled.a`
  width: 100%;
  color: inherit;
  text-decoration: none;

  &:hover {
    color: inherit;
  }

  &:visited {
    color: inherit;
  }

  &:active {
    color: inherit;
  }
`
